/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaChevronLeft } from 'react-icons/fa';
import AppModal from '../../shared/components/app-modal/AppModal';
import AppLoading from '../../shared/components/app-loading/AppLoading';
import AppAlert from '../../shared/components/app-alert/AppAlert';
import AppButton from '../../shared/components/app-button/AppButton';
import {
  dolphinInsuranceCancellationApi,
  dolphinInsuranceRescheduleApi,
} from '../../api/tboInsuranceApi';
import { NAVIGATION_ROUTES } from '../../shared/constants/Constants';
import { formatDate, getNewDate } from '../../shared/helper-utils/appUtils';
import { getInsuranceBookingDetails } from '../../api/profileApi';
import AppDatePicker from '../../shared/components/app-date-picker/AppDatePicker';
import './MyBookingsPage.scss';

const STATUS_STYLES = {
  confirmed: { bg: 'rgba(25, 135, 84, 0.1)', fg: '#198754', dot: '#198754' },
  confirm: { bg: 'rgba(25, 135, 84, 0.1)', fg: '#198754', dot: '#198754' },
  success: { bg: 'rgba(25, 135, 84, 0.1)', fg: '#198754', dot: '#198754' },
  pending: { bg: 'rgba(255, 193, 7, 0.15)', fg: '#ffc107', dot: '#ffc107' },
  cancelled: { bg: 'rgba(220, 53, 69, 0.1)', fg: '#dc3545', dot: '#dc3545' },
  failed: { bg: 'rgba(220, 53, 69, 0.1)', fg: '#dc3545', dot: '#dc3545' },
};

function StatusBadge({ status }) {
  const key = (status || '').toLowerCase();
  const style = STATUS_STYLES[key] || {
    bg: 'rgba(108, 117, 125, 0.1)',
    fg: '#6c757d',
    dot: '#6c757d',
  };
  return (
    <span
      className="d-inline-flex align-items-center gap-2"
      style={{
        background: style.bg,
        color: style.fg,
        fontWeight: 600,
        fontSize: '0.76rem',
        padding: '3px 10px',
        borderRadius: '999px',
        lineHeight: 1.6,
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: style.dot,
          display: 'inline-block',
        }}
      />
      {status || 'N/A'}
    </span>
  );
}

function InfoRow({ label, value, last }) {
  if (!value) return null;
  return (
    <div
      className="d-flex align-items-start justify-content-between py-2"
      style={{ borderBottom: last ? 'none' : '1px solid var(--background100)' }}
    >
      <span className="fs-12" style={{ color: 'var(--darkGrey)' }}>
        {label}
      </span>
      <span
        className="fs-13 fw-600"
        style={{
          color: 'var(--textBlack)',
          textAlign: 'right',
          maxWidth: '62%',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="flight-cancellation-page_container_card">
      <div className="flight-cancellation-page_container_card_title fw-600 d-flex align-items-center gap-2 m-0 fs-14 text-dark">
        {icon}
        <span>{title}</span>
      </div>
      <div className="flight-cancellation-page_container_card_content p-3">
        {children}
      </div>
    </div>
  );
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

function parseInsuranceDates(details) {
  if (!details) return {};
  if (details.startdate || details.enddate) {
    const fmt = (d) => {
      if (!d) return null;
      try {
        return formatDate(getNewDate(d), 'DD/MM/YYYY');
      } catch (e) {
        return d;
      }
    };
    return {
      startDate: fmt(details.startdate),
      endDate: fmt(details.enddate),
    };
  }
  const insuranceDetails = details.InsuranceDetails;
  if (!insuranceDetails) return {};
  const parts = insuranceDetails.split('###');
  return {
    startDate: parts[1] || null,
    endDate: parts[2] || null,
  };
}

function parsePaxDetails(details) {
  if (!details) return [];
  const p = details.TravelInsurancePassengers?.TravelInsurancePassenger;
  if (!p) return [];

  const rawList = Array.isArray(p) ? p : [p];
  return rawList.map((item, index) => ({
    id: item.PaxId || item.paxId || item.SrNo || index,
    paxname: item.PaxName || '',
    age: item.Age || '',
    dob: item.DOB || '',
    mobile: item.Mobile || '',
    phoneno: item.PhoneNo || '',
    email: item.EmailAddress || '',
    emailaddress: item.EmailAddress || '',
    city: item.City || '',
    state: item.State || '',
    stateName: item.State || '',
    pincode: item.Pincode || '',
    country: item.Country || '',
    passportnumber: item.Passport || '',
    nominee: item.Nominee || '',
    relation: item.Relation || '',
  }));
}

export default function InsuranceCancelPage() {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleStartDate, setRescheduleStartDate] = useState(null);
  const [rescheduleEndDate, setRescheduleEndDate] = useState(null);
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);

  const fetchDetails = async () => {
    if (!transactionId) return;
    const res = await getInsuranceBookingDetails(transactionId);
    const record =
      res?.Root?.Result?.TravelInsuranceInvoice ||
      res?.Result?.TravelInsuranceInvoice;
    if (record) setDetails(record);
  };

  useEffect(() => {
    fetchDetails();
  }, [transactionId]);

  const performCancel = async () => {
    if (!transactionId) return;
    try {
      setIsLoading(true);
      const res = await dolphinInsuranceCancellationApi(transactionId);
      const status = res?.ApiStatus?.Status || res?.Root?.Status;
      const message = res?.ApiStatus?.Message || res?.Root?.Message;
      if (res && status === 'OK') {
        setAlert({
          variant: 'success',
          title: 'Cancelled',
          description: message || 'Insurance cancelled',
        });
        setTimeout(() => navigate(NAVIGATION_ROUTES.MY_BOOKINGS_ROUTE), 1200);
      } else {
        setAlert({
          variant: 'error',
          title: 'Error',
          description: message || 'Cancellation failed',
        });
      }
    } catch (err) {
      setAlert({
        variant: 'error',
        title: 'Error',
        description: 'Something went wrong. Try again later.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const performReschedule = async () => {
    if (!transactionId || !rescheduleStartDate || !rescheduleEndDate) return;
    try {
      setIsLoading(true);
      const fmt = (d) => formatDate(getNewDate(d), 'YYYY-MM-DD');
      const res = await dolphinInsuranceRescheduleApi(
        transactionId,
        fmt(rescheduleStartDate),
        fmt(rescheduleEndDate),
      );
      const status = res?.ApiStatus?.Status || res?.Root?.Status;
      const message = res?.ApiStatus?.Message || res?.Root?.Message;
      const result = res?.ApiStatus?.Result || '';
      if (status === 'OK' && result?.toLowerCase() === 'success') {
        setAlert({
          variant: 'success',
          title: 'Rescheduled',
          description: message || 'Policy dates updated successfully.',
        });
        setShowReschedule(false);
        fetchDetails();
      } else {
        setAlert({
          variant: 'error',
          title: 'Error',
          description: message || 'Reschedule failed. Please try again.',
        });
      }
    } catch (err) {
      setAlert({
        variant: 'error',
        title: 'Error',
        description: 'Something went wrong. Try again later.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const insuranceDates = details ? parseInsuranceDates(details) : {};
  const passengers = details ? parsePaxDetails(details) : [];

  const formatTxDate = (txDate) => {
    if (!txDate) return 'N/A';
    try {
      return formatDate(getNewDate(txDate.split(' ')[0]), 'DD/MM/YYYY');
    } catch (e) {
      return txDate;
    }
  };

  const formatPickerDate = (d) =>
    d ? formatDate(getNewDate(d), 'DD/MM/YYYY') : '—';

  return (
    <>
      <AppLoading isLoading={isLoading} />
      {alert && (
        <AppAlert
          showIcon
          variant={alert.variant}
          title={alert.title}
          description={alert.description}
          onHide={() => setAlert(null)}
        />
      )}

      <div className="flight-cancellation-page" style={{ minHeight: '100vh' }}>
        <div className="flight-cancellation-page_container py-2 px-3">
          {/* ── Breadcrumb ── */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1"
              style={{ color: 'var(--primaryColor)', fontSize: '0.82rem' }}
              onClick={() =>
                navigate('/my-bookings?transactionType=insuranceBookings')
              }
            >
              <FaChevronLeft size={10} />
              My Bookings
            </button>
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              /
            </span>
            <span className="fs-12" style={{ color: 'var(--darkGrey)' }}>
              Insurance Cancellation
            </span>
          </div>

          {details ? (
            <>
              {/* ── Page header ── */}
              <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                <h1
                  className="fw-700 m-0"
                  style={{
                    fontSize: '1.4rem',
                    color: 'var(--textBlack)',
                  }}
                >
                  {details.InsuranceName || 'Travel Insurance'}
                </h1>
                <StatusBadge status={details.BookingStatus} />
              </div>

              {/* ── Two column dashboard layout ── */}
              <div className="row g-3">
                {/* LEFT: informational content */}
                <div className="col-12 col-lg-7">
                  <div className="mb-3">
                    <SectionCard title="Booking Summary">
                      <InfoRow label="Transaction ID" value={details.Txid} />
                      {details.InsuranceName && (
                        <InfoRow
                          label="Insurance Plan"
                          value={details.InsuranceName}
                        />
                      )}
                      <InfoRow
                        label="Booking Date"
                        value={formatTxDate(details.TxDate)}
                      />
                      {details.PolicyNumber && (
                        <InfoRow
                          label="Policy Number"
                          value={details.PolicyNumber}
                        />
                      )}
                      <InfoRow
                        label="Payment Mode"
                        value={
                          details.PaymentMode
                            ? details.PaymentMode.charAt(0).toUpperCase() +
                              details.PaymentMode.slice(1)
                            : null
                        }
                      />
                      {details.InvoiceNumber && (
                        <InfoRow
                          label="Invoice Number"
                          value={
                            details.InvoiceNumber &&
                            details.InvoiceNumber !== '-'
                              ? details.InvoiceNumber
                              : null
                          }
                        />
                      )}
                      <InfoRow
                        label="Payment Status"
                        value={details.PaymentStatus}
                        last
                      />
                    </SectionCard>
                  </div>

                  {passengers.length > 0 && (
                    <SectionCard title="Passenger Details">
                      {passengers.map((pax, index) => (
                        <div
                          key={pax.id || index}
                          className={index > 0 ? 'mt-4 pt-3' : ''}
                          style={
                            index > 0
                              ? { borderTop: '1px solid var(--background100)' }
                              : {}
                          }
                        >
                          <div
                            className="d-flex align-items-center gap-3 mb-3 pb-3"
                            style={{
                              borderBottom: '1px solid var(--background100)',
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: 'var(--primaryColor10)',
                                color: 'var(--primaryColor)',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(pax.paxname)}
                            </div>
                            <div>
                              <div
                                className="fw-700 fs-14"
                                style={{
                                  color: 'var(--textBlack)',
                                }}
                              >
                                {pax.paxname}
                              </div>
                              <div className="fs-12 text-muted">
                                {index === 0
                                  ? 'Lead Passenger'
                                  : `Passenger ${index + 1}`}
                              </div>
                            </div>
                          </div>

                          <InfoRow
                            label="Age / DOB"
                            value={`${pax.age || ''}${
                              pax.age && pax.dob ? ' / ' : ''
                            }${pax.dob || ''}`}
                          />
                          <InfoRow
                            label="Mobile"
                            value={pax.mobile || pax.phoneno}
                          />
                          <InfoRow
                            label="Email"
                            value={pax.emailaddress || pax.email}
                          />
                          <InfoRow
                            label="City / State"
                            value={[pax.city, pax.state || pax.stateName]
                              .filter(Boolean)
                              .join(', ')}
                          />
                          <InfoRow label="Pincode" value={pax.pincode} />
                          <InfoRow label="Country" value={pax.country} />
                          <InfoRow
                            label="Passport No."
                            value={pax.passportnumber}
                          />
                          <InfoRow
                            label="Nominee / Relation"
                            value={[pax.nominee, pax.relation]
                              .filter(Boolean)
                              .join(' / ')}
                            last
                          />
                        </div>
                      ))}
                    </SectionCard>
                  )}
                </div>

                {/* RIGHT: live summary + actions */}
                <div className="col-12 col-lg-5">
                  {(insuranceDates.startDate || insuranceDates.endDate) && (
                    <div className="mb-3">
                      <SectionCard title="Travel Period">
                        <div className="d-flex align-items-center justify-content-between px-2 py-1">
                          <div>
                            <div
                              className="fs-10 text-muted text-uppercase"
                              style={{ letterSpacing: '0.04em' }}
                            >
                              From
                            </div>
                            <div
                              className="fs-14 fw-700"
                              style={{ color: 'var(--textBlack)' }}
                            >
                              {insuranceDates.startDate || '—'}
                            </div>
                          </div>
                          <FaArrowRight size={12} className="text-muted" />
                          <div className="text-end">
                            <div
                              className="fs-10 text-muted text-uppercase"
                              style={{ letterSpacing: '0.04em' }}
                            >
                              To
                            </div>
                            <div
                              className="fs-14 fw-700"
                              style={{ color: 'var(--textBlack)' }}
                            >
                              {insuranceDates.endDate || '—'}
                            </div>
                          </div>
                        </div>
                      </SectionCard>
                    </div>
                  )}

                  <div className="mb-3">
                    <SectionCard title="Fare Summary">
                      <InfoRow
                        label="Basic Fare"
                        value={`₹ ${parseFloat(details.BaseFare || 0).toFixed(
                          2,
                        )}`}
                      />
                      <InfoRow
                        label="Taxes"
                        value={`₹ ${parseFloat(details.Tax || 0).toFixed(2)}`}
                      />
                      <InfoRow
                        label="Service Charges"
                        value={`₹ ${parseFloat(details.ServiceTax || 0).toFixed(
                          2,
                        )}`}
                      />
                      {details.HandlingCharges > 0 && (
                        <InfoRow
                          label="Handling Charges"
                          value={`₹ ${parseFloat(
                            details.HandlingCharges,
                          ).toFixed(2)}`}
                        />
                      )}

                      {/* Signature element: highlighted total */}
                      <div
                        className="d-flex align-items-center justify-content-between mt-3"
                        style={{
                          background: 'var(--primaryColor10)',
                          border: '1px solid var(--primaryColor20)',
                          borderRadius: '10px',
                          padding: '10px 14px',
                        }}
                      >
                        <span
                          className="fs-12 fw-700"
                          style={{ color: 'var(--textBlack)' }}
                        >
                          Total Billing Amount
                        </span>
                        <span
                          className="fs-16 fw-800"
                          style={{ color: 'var(--primaryColor)' }}
                        >
                          ₹ {parseFloat(details.TotalInvoice || 0).toFixed(2)}
                        </span>
                      </div>
                    </SectionCard>

                    {/* Manage Booking Buttons below Fare Summary */}
                    <div className="d-flex gap-3 mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-danger flex-1"
                        style={{
                          height: '48px',
                          borderRadius: '10px',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                        onClick={() => setShowConfirm(true)}
                      >
                        Cancel Insurance
                      </button>
                      <AppButton
                        variant="primary"
                        btnText="Reschedule Insurance"
                        className="flex-1"
                        onClick={() =>
                          setShowRescheduleForm(!showRescheduleForm)
                        }
                      />
                    </div>

                    {/* Conditional Reschedule Inputs */}
                    {showRescheduleForm && (
                      <div className="mt-3">
                        <SectionCard
                          title="Reschedule Insurance"
                          icon={
                            <FaCalendarAlt
                              size={13}
                              style={{ color: 'var(--primaryColor)' }}
                            />
                          }
                        >
                          <p className="fs-12 text-muted mb-3">
                            Select new travel start and end dates.
                          </p>
                          <div className="mb-4">
                            <AppDatePicker
                              name="reschedule-start"
                              label="New Start Date"
                              selectedDate={rescheduleStartDate}
                              onChange={(d) => setRescheduleStartDate(d)}
                              placeholder="DD/MM/YYYY"
                              minDate={new Date()}
                              maxDate={rescheduleEndDate || undefined}
                              customClassName="my-bookings-page_date-picker"
                              labelClassName="my-bookings-page_date-label"
                            />
                          </div>
                          <div className="mb-4">
                            <AppDatePicker
                              name="reschedule-end"
                              label="New End Date"
                              selectedDate={rescheduleEndDate}
                              onChange={(d) => setRescheduleEndDate(d)}
                              placeholder="DD/MM/YYYY"
                              minDate={rescheduleStartDate || new Date()}
                              customClassName="my-bookings-page_date-picker"
                              labelClassName="my-bookings-page_date-label"
                            />
                          </div>
                          <AppButton
                            variant="primary"
                            btnText={
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                <span>Confirm Reschedule</span>
                              </div>
                            }
                            className="w-100 app-btn-small"
                            disabled={
                              !rescheduleStartDate || !rescheduleEndDate
                            }
                            onClick={() => setShowReschedule(true)}
                          />
                        </SectionCard>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
              <div
                className="spinner-border mb-3"
                role="status"
                style={{
                  width: '1.75rem',
                  height: '1.75rem',
                  color: 'var(--primaryColor)',
                }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mb-0 fs-14">Fetching insurance record...</p>
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <AppModal
          modalTitle="Confirm Cancellation"
          showCloseButton
          showFooter
          okText="Yes, Cancel"
          cancelText="No, Keep Booking"
          onModalClose={(confirmed) => {
            setShowConfirm(false);
            if (confirmed) performCancel();
          }}
        >
          <div className="py-1">
            <p className="mb-2 fs-14">
              Are you sure you want to cancel this travel insurance?
            </p>
            {details?.Txid && (
              <p className="mb-0 fs-12 text-muted">
                Transaction: <strong>{details.Txid}</strong>
              </p>
            )}
          </div>
        </AppModal>
      )}

      {showReschedule && (
        <AppModal
          modalTitle="Confirm Reschedule"
          showCloseButton
          showFooter
          okText="Yes, Reschedule"
          cancelText="Cancel"
          onModalClose={(confirmed) => {
            setShowReschedule(false);
            if (confirmed) performReschedule();
          }}
        >
          <div className="py-1">
            <p className="mb-2 fs-14">
              Reschedule the insurance policy from{' '}
              <strong>{formatPickerDate(rescheduleStartDate)}</strong> to{' '}
              <strong>{formatPickerDate(rescheduleEndDate)}</strong>?
            </p>
            {details?.Txid && (
              <p className="mb-0 fs-12 text-muted">
                Transaction: <strong>{details.Txid}</strong>
              </p>
            )}
          </div>
        </AppModal>
      )}
    </>
  );
}
